import { useEffect, useState, type ReactNode } from "react";
import { itemById } from "@zend/content";
import { pick, useT } from "../../i18n/index.js";
import { useBalances } from "../../state/balances.js";
import { useSession } from "../../state/session.js";
import { Button } from "../../ui/Button.js";
import { formatCompact } from "../../ui/Counter.js";
import { Icon as FunctionalIcon } from "../../ui/Icon.js";
import { baseApi, type Cost, type CoverPlan, type ResourceKey } from "../api.js";
import { Reasons } from "./shared.js";

/** The VIP level from which the normal start button covers missing costs from crates on its own (D75). */
export const AUTO_COVER_VIP = 4;
const RESOURCE_ORDER: ResourceKey[] = ["RAT", "POL", "ALY", "ENC", "SCR"];

function shortfallOf(cost: Partial<Cost>, balances: Record<ResourceKey, number> | null): Partial<Record<ResourceKey, number>> {
  const short: Partial<Record<ResourceKey, number>> = {};
  if (!balances) return short;
  for (const k of RESOURCE_ORDER) {
    const need = cost[k] ?? 0;
    if (need > (balances[k] ?? 0)) short[k] = need - (balances[k] ?? 0);
  }
  return short;
}

/**
 * One paid action (build, upgrade, research, reclaim) with its cost check (D75): when the wallet is short the row says
 * what is missing, whether the depot's crates cover it and offers "Cover from depot & start". From VIP 4 with the
 * setting on, the normal button covers on its own. `reasons` are the server's other blockers; "insufficient resources"
 * is replaced by this row's own line.
 */
export function PayAction({ cost, reasons, onStart, label, variant = "primary", testId, style, showReasons = true }: {
  cost: Partial<Cost>;
  reasons: string[];
  onStart: (cover: boolean) => Promise<unknown> | void;
  label: ReactNode;
  variant?: "primary" | "gold";
  testId: string;
  style?: React.CSSProperties;
  /** Some panels print the blockers elsewhere (HQ gate); the buttons still honour them. */
  showReasons?: boolean;
}) {
  const t = useT();
  const balances = useBalances((s) => s.balances);
  const vip = useSession((s) => s.vip?.level ?? 0);
  const autoSetting = useSession((s) => s.settings?.autoCoverCosts ?? false);
  const short = shortfallOf(cost, balances);
  const isShort = Object.keys(short).length > 0;
  const otherReasons = reasons.filter((r) => r !== "insufficient resources");
  const blocked = otherReasons.length > 0;
  const [plan, setPlan] = useState<CoverPlan | null>(null);
  const key = JSON.stringify(cost);
  useEffect(() => {
    if (!isShort) {
      setPlan(null);
      return;
    }
    let alive = true;
    baseApi.coverPlan(cost).then((p) => { if (alive) setPlan(p); }).catch(() => { if (alive) setPlan(null); });
    return () => { alive = false; };
  }, [key, isShort, balances]);
  const auto = vip >= AUTO_COVER_VIP && autoSetting;
  const coverable = isShort && plan?.sufficient === true;
  const canStart = !blocked && (!isShort || (auto && coverable));
  return (
    <div className="pay-action" data-testid={`${testId}-pay`} data-short={isShort || undefined} data-coverable={coverable || undefined}>
      {isShort ? (
        <p className="pay-cover" data-testid={`${testId}-cover-hint`}>
          <span className="pay-missing">
            <FunctionalIcon name="warning" /> {t("cover.missing")}{" "}
            {RESOURCE_ORDER.filter((k) => short[k]).map((k) => `${formatCompact(short[k]!)} ${t(`res.${k}`)}`).join(" · ")}
          </span>
          {plan === null ? <span className="hint">{t("common.loading")}</span> : plan.sufficient ? (
            <span className="pay-plan ok" data-testid={`${testId}-cover-ok`}>
              <FunctionalIcon name="check" /> {t("cover.fromDepot")} {plan.opens.map((o) => `${o.qty}× ${pick(itemById(o.itemId).name)}`).join(", ")} → {t("cover.enough")}
            </span>
          ) : (
            <span className="pay-plan short" data-testid={`${testId}-cover-short`}>
              <FunctionalIcon name="close" /> {t("cover.notEnough")}{" "}
              {RESOURCE_ORDER.filter((k) => plan.missing[k]).map((k) => `${formatCompact(plan.missing[k]!)} ${t(`res.${k}`)}`).join(" · ")}
              {plan.blocked.length ? ` · ${t("cover.storage")}` : ""}
            </span>
          )}
        </p>
      ) : null}
      {showReasons ? <Reasons reasons={otherReasons} /> : null}
      <div className="row" style={{ marginTop: 8, gap: 8, flexWrap: "wrap", ...style }}>
        <Button variant={variant} disabled={!canStart} onClick={() => void onStart(isShort && auto)} data-testid={testId}>
          {label}{isShort && auto && coverable ? <small className="pay-auto"> · {t("cover.autoTag")}</small> : null}
        </Button>
        {isShort && !auto ? (
          <Button variant="gold" disabled={blocked || !coverable} onClick={() => void onStart(true)} data-testid={`${testId}-cover`}>
            <FunctionalIcon name="inventory" /> {t("cover.coverAndStart")}
          </Button>
        ) : null}
      </div>
      {isShort && vip < AUTO_COVER_VIP ? <p className="hint pay-vip">{t("cover.vipHint", { n: AUTO_COVER_VIP })}</p> : null}
    </div>
  );
}

